import { useCallback, useEffect, useMemo, useState } from "react";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import { GET_ORGANIZATIONS_BRANDS_LOOKUP } from "@src/domain/graphql/getOrganizationsBrandsLookup";
import type {
  OrganizationsBrandsLookupItem,
  OrganizationsBrandsLookupResponse,
} from "@src/domain/dto/organizationsBrands";

export interface OrganizationsBrandsLookupSlice {
  organizationsBrandsLookup: OrganizationsBrandsLookupItem[] | null;
  organizationsBrandsLookupCount: number;
  organizationsBrandsLookupLoading: boolean;
  organizationsBrandsLookupError: string | null;
  refetchOrganizationsBrandsLookup: () => Promise<void>;
}

export function useOrganizationsBrandsLookup(): OrganizationsBrandsLookupSlice {
  const [organizationsBrandsLookup, setOrganizationsBrandsLookup] = useState<
    OrganizationsBrandsLookupItem[] | null
  >(null);
  const [organizationsBrandsLookupCount, setOrganizationsBrandsLookupCount] =
    useState(0);
  const [organizationsBrandsLookupLoading, setOrganizationsBrandsLookupLoading] =
    useState(true);
  const [organizationsBrandsLookupError, setOrganizationsBrandsLookupError] =
    useState<string | null>(null);

  const refetchOrganizationsBrandsLookup = useCallback(async () => {
    setOrganizationsBrandsLookupLoading(true);
    setOrganizationsBrandsLookupError(null);
    try {
      const client = GraphQlAdapter.getInstance().getClient();
      const result = await client.query<OrganizationsBrandsLookupResponse>({
        query: GET_ORGANIZATIONS_BRANDS_LOOKUP,
      });
      const data = result.data?.organizationsBrandsLookup;
      if (data) {
        setOrganizationsBrandsLookup(data.items ?? null);
        setOrganizationsBrandsLookupCount(data.count ?? 0);
      } else {
        setOrganizationsBrandsLookup(null);
        setOrganizationsBrandsLookupCount(0);
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to load organizations and brands";
      setOrganizationsBrandsLookupError(message);
      setOrganizationsBrandsLookup(null);
      setOrganizationsBrandsLookupCount(0);
    } finally {
      setOrganizationsBrandsLookupLoading(false);
    }
  }, []);

  useEffect(() => {
    refetchOrganizationsBrandsLookup();
  }, [refetchOrganizationsBrandsLookup]);

  return useMemo(
    () => ({
      organizationsBrandsLookup,
      organizationsBrandsLookupCount,
      organizationsBrandsLookupLoading,
      organizationsBrandsLookupError,
      refetchOrganizationsBrandsLookup,
    }),
    [
      organizationsBrandsLookup,
      organizationsBrandsLookupCount,
      organizationsBrandsLookupLoading,
      organizationsBrandsLookupError,
      refetchOrganizationsBrandsLookup,
    ],
  );
}
